/* eslint-disable no-process-env */
import { isServerSide } from 'helpers/isServerSide';

export const IS_PRODUCTION_MODE = process.env.NODE_ENV === 'production';

const getEnv = (key) => {
	const value = isServerSide() ? process.env[key] : window.ENV?.[key];
	if (value === 'true') return true;
	if (value === 'false') return false;
	if (!isNaN(value) && value.trim() !== '') return Number(value);
	return value;
};

export const isFeatureEnabled = (value) => value !== false;

/**
 * This privateEnv vars aren't casted to boolean or number, they are always strings
 */
export const privateEnvConfig = Object.freeze({
	API_ROUTE_SECRET: process.env.API_ROUTE_SECRET,
	JWT_KEY: process.env.JWT_KEY,
	SERVER_RESTURL_BASE: process.env.SERVER_RESTURL_BASE,
	SPLIT_KEY: process.env.SPLIT_KEY,
});

/* These variables are meant to be public, no problem in exposing them to the browser
 * (This is something similar to nextjs NEXT_PUBLIC_*)
 */
const publicConfig = {
	browser: {
		BASE_URL: getEnv('BASE_URL'),
		AUDIOEYE_ENABLED: getEnv('AUDIOEYE_ENABLED'),
		AUDIOEYE_URL: getEnv('AUDIOEYE_URL'),
		BUGSNAG_BROWSER_API_KEY: getEnv('BUGSNAG_BROWSER_API_KEY'),
		BUGSNAG_ENABLED: getEnv('BUGSNAG_ENABLED'),
		DEBUG_MODE: getEnv('DEBUG_MODE'),
		ENABLE_RACK_MINIPROFILER: getEnv('ENABLE_RACK_MINIPROFILER'),
		DEAL_DRIVER_SLACK_CONFIRMATION_CHANNEL_URL: getEnv('DEAL_DRIVER_SLACK_CONFIRMATION_CHANNEL_URL'),
		GTM_ENABLED: getEnv('GTM_ENABLED'),
		GTM_ID: getEnv('GTM_ID'),
		IMAGE_BASE_URL: getEnv('IMAGE_BASE_URL'),
		INTERCOM_APP_ID: getEnv('INTERCOM_APP_ID'),
		INTERCOM_ENABLED: getEnv('INTERCOM_ENABLED'),
		MARKETING_SMS: getEnv('MARKETING_SMS'),
		NEXT_PUBLIC_NO_AXIOS_TIMEOUT: getEnv('NEXT_PUBLIC_NO_AXIOS_TIMEOUT'),
		ORTTO_ENABLED: getEnv('ORTTO_ENABLED'),
		OKTA_CLIENT_ID: getEnv('OKTA_CLIENT_ID'),
		OKTA_ISSUER: getEnv('OKTA_ISSUER'),
		PUSHER_APP_KEY: getEnv('PUSHER_APP_KEY'),
		PUSHER_ENABLED: getEnv('PUSHER_ENABLED'),
		RECAPTCHA_ENABLED: getEnv('RECAPTCHA_ENABLED'),
		RECAPTCHA_V2_SITE_KEY: getEnv('RECAPTCHA_V2_SITE_KEY'),
		RECAPTCHA_V3_SITE_KEY: getEnv('RECAPTCHA_V3_SITE_KEY'),
		RELATIVE_BASE_URL: getEnv('RELATIVE_BASE_URL'),
		RELEASE_STAGE: getEnv('RELEASE_STAGE'),
		RESTURL_BASE: getEnv('RESTURL_BASE'),
		SEGMENT_WRITE_KEY: getEnv('SEGMENT_WRITE_KEY'),
		SEGMENT_ENABLED: getEnv('SEGMENT_ENABLED'),
		SOCKET_IO_URL: getEnv('SOCKET_IO_URL'),
		STOCKWAVE_SCRIPT_URL: getEnv('STOCKWAVE_SCRIPT_URL'),
		STOCKWAVE_ENABLED: getEnv('STOCKWAVE_ENABLED'),
		VERSION: getEnv('VERSION'),
		GOOGLE_PLACES_API_KEY: getEnv('GOOGLE_PLACES_API_KEY'),
		PAYSTAND_SCRIPT_URL: getEnv('PAYSTAND_SCRIPT_URL'),
		PAYSTAND_PUBLISHABLE_KEY: getEnv('PAYSTAND_PUBLISHABLE_KEY'),
		PAYSTAND_EXTERNAL_CUSTOMER_ID: getEnv('PAYSTAND_EXTERNAL_CUSTOMER_ID'),
		PAYSTAND_SUBSIDIARY_ID: getEnv('PAYSTAND_SUBSIDIARY_ID'),
		NEXT_PUBLIC_ORTTO_TRACKING_ID: getEnv('NEXT_PUBLIC_ORTTO_TRACKING_ID'),
		NEXT_PUBLIC_ORTTO_CAPTURE_API_URL: getEnv('NEXT_PUBLIC_ORTTO_CAPTURE_API_URL'),
		HELLO_SIGN_CLIENT_ID: getEnv('HELLO_SIGN_CLIENT_ID'),
		MARKET_GUIDE_PRICING_FORECAST_API: getEnv('MARKET_GUIDE_PRICING_FORECAST_API'),
	},
	server: {
		BUGSNAG_SERVER_API_KEY: getEnv('BUGSNAG_SERVER_API_KEY'),
		NEXT_RUNTIME: getEnv('NEXT_RUNTIME'),
		NODE_ENV: getEnv('NODE_ENV'),
		PAPERTRAIL_ENABLED: getEnv('PAPERTRAIL_ENABLED'),
		PAPERTRAIL_HOST: getEnv('PAPERTRAIL_HOST'),
		PAPERTRAIL_PORT: getEnv('PAPERTRAIL_PORT'),
		STATIC_PROPS_REVALIDATE_TIME: getEnv('STATIC_PROPS_REVALIDATE_TIME'),
		STORYBOOK: getEnv('STORYBOOK'),
		FEATURE_FLAGS_CACHE_TTL_SECONDS: getEnv('FEATURE_FLAGS_CACHE_TTL_SECONDS'),
	},
};

export const publicEnvConfig = Object.freeze({ ...publicConfig.browser, ...publicConfig.server });

const getPublicRailsBaseUrl = () => publicEnvConfig.RESTURL_BASE;

const getServerRailsBaseUrl = () => privateEnvConfig.SERVER_RESTURL_BASE || getPublicRailsBaseUrl();

export const getRailsBaseUrl = () => (isServerSide() ? getServerRailsBaseUrl() : getPublicRailsBaseUrl());

export const getBrowserEnvVariables = () => {
	const envVariables = Object.freeze({
		...Object.keys(publicConfig.browser).reduce((acc, key) => {
			acc[key] = process.env[key];
			return acc;
		}, {}),
	});

	return `window.ENV = ${JSON.stringify(envVariables)};`;
};
